"use client";

import { useReportWebVitals } from "next/web-vitals";
import {
  captureClientEvent,
  captureClientHistogram,
} from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { createClientLogger } from "@/lib/logging/client";
import { telemetryRouteGroup } from "@/lib/telemetry/route-group";

const logger = createClientLogger("components/providers/web-vitals-reporter");
const millisecondMetrics = new Set(["FCP", "INP", "LCP", "TTFB"]);

type WebVitalExtras = {
  rating?: string;
  navigationType?: string;
};

export function WebVitalsReporter() {
  useReportWebVitals((metric) => {
    try {
      const extras = metric as typeof metric & WebVitalExtras;
      const value = Math.round(metric.value * 100) / 100;
      const routePath =
        typeof window !== "undefined" ? window.location.pathname : undefined;

      const attributes = {
        metric_name: metric.name,
        metric_id: metric.id,
        metric_label: metric.label,
        metric_value: value,
        ...(millisecondMetrics.has(metric.name) ? { duration_ms: value } : {}),
        ...(metric.name === "CLS" ? { score: value } : {}),
        ...(extras.rating ? { rating: extras.rating } : {}),
        ...(extras.navigationType
          ? { navigation_type: extras.navigationType }
          : {}),
        route_path: routePath,
      };

      logger.info("web_vitals.metric", attributes);

      // Also an event, not just a log: only events can be charted as a p75
      // trend, broken down by route/surface/release, and alerted on. A log line
      // tells you a single page was slow; the event tells you a deploy
      // regressed LCP on Android.
      captureClientEvent(ANALYTICS_EVENTS.WEB_VITAL_MEASURED, attributes);

      // Metrics give the operations view cheap p50/p95/p99 series. The event
      // above remains canonical because it carries the exact path, session,
      // person, release, and replay linkage needed for investigation.
      captureClientHistogram(
        `web.vital.${metric.name.toLowerCase()}`,
        value,
        metric.name === "CLS" ? "1" : "ms",
        {
          route_group: telemetryRouteGroup(routePath),
          ...(extras.rating ? { rating: extras.rating } : {}),
          ...(extras.navigationType
            ? { navigation_type: extras.navigationType }
            : {}),
        },
      );
    } catch {
      // Web vitals must never crash the booking flow.
    }
  });

  return null;
}
